import React from 'react';

import { cn } from '../../base/utils';

import { SkeletonScope, SkeletonItem, type SkeletonGroupAnimation } from '../../skeleton/motion';

import { Skeleton } from '../Skeleton';
import { SkeletonText } from './SkeletonText';

export interface SkeletonListProps extends React.HTMLAttributes<HTMLDivElement> {
  animation?: SkeletonGroupAnimation;
  /**
   * 列表项数量
   * @default 5
   */
  count?: number;
  /**
   * 是否显示头像/图标
   * @default false
   */
  showAvatar?: boolean;
  /**
   * 头像形状
   * @default 'circle'
   */
  avatarShape?: 'circle' | 'square';
  /**
   * 是否显示副标题
   * @default false
   */
  showSubtitle?: boolean;
  /**
   * 是否显示右侧操作
   * @default false
   */
  showAction?: boolean;
  /**
   * 列表项间距
   * @default 'md'
   */
  spacing?: 'sm' | 'md' | 'lg';
  /**
   * 是否显示分割线
   * @default false
   */
  showDivider?: boolean;
}

const spacingMap = {
  sm: 'py-2',
  md: 'py-3',
  lg: 'py-4',
};

/**
 * 列表骨架屏组件
 */
export function SkeletonList({
  className,
  animation,
  count = 5,
  showAvatar = false,
  avatarShape = 'circle',
  showSubtitle = false,
  showAction = false,
  spacing = 'md',
  showDivider = false,
  ...props
}: SkeletonListProps) {
  return (
    <SkeletonScope animation={animation}>
      <div className={cn('w-full', className)} {...props}>
        {Array.from({ length: count }).map((_, index) => (
          <SkeletonItem key={index} index={index}>
            <div
              className={cn(
                'flex items-center gap-3',
                spacingMap[spacing],
                showDivider && index !== count - 1 && 'border-b',
              )}
            >
              {/* 头像/图标 */}
              {showAvatar && (
                <Skeleton
                  className={cn(
                    'size-10 shrink-0',
                    avatarShape === 'circle' ? 'rounded-full' : 'rounded-md',
                  )}
                />
              )}

              {/* 内容区域 */}
              <div className="min-w-0 flex-1">
                <SkeletonText lines={1} size="md" lastLineWidth={50 + Math.random() * 30} />
                {showSubtitle && (
                  <div className="mt-1">
                    <SkeletonText lines={1} size="sm" lastLineWidth={30 + Math.random() * 40} />
                  </div>
                )}
              </div>

              {/* 右侧操作 */}
              {showAction && <Skeleton className="h-8 w-16 shrink-0 rounded-md" />}
            </div>
          </SkeletonItem>
        ))}
      </div>
    </SkeletonScope>
  );
}
